import type {
  BaseEntity,
  SimpleTableConfig,
  UniversalInlineEditHandler,
  FieldValue,
  SimpleFieldConfig,
  EntityHooks,
} from "../../../types";
import type { SimpleTableContextValue } from "../../../contexts/SimpleTableContext";
import type { SpecializedCellProps } from "../tableCellSpecializedFields.types";
import type { AuthenticatedUser } from "@/lib/types/unified";

/**
 * Everything the cell router is handed for one cell.
 *
 * Split out of `TableCellRouter.tsx` (module-size lane L17); re-exported from
 * there so the public import path is unchanged.
 */
export interface CellRendererProps<TData extends BaseEntity> {
  fieldId: string;
  fieldValue: FieldValue;
  fieldConfig: SimpleFieldConfig;
  item: TData;
  isEditing: boolean;
  isTextareaField: boolean;
  config: SimpleTableConfig<TData>;
  state: SimpleTableContextValue<TData>["state"];
  callbacks: SimpleTableContextValue<TData>["callbacks"];
  auth: { appUser: AuthenticatedUser | null; roleName: string | null };
  data: TData[];
  inlineEditHandler: UniversalInlineEditHandler;
  onOpenDetailModal?: (item: TData, ids: string[], index: number) => void;
  dataRowIds?: string[];
  dataIndex?: number;
  hooks?: EntityHooks<TData>;
  onEdit?: (item: TData) => void;
  onView?: (item: TData) => void;
  onDelete?: (item: TData) => void;
}

/**
 * Narrow the router's props to the block EVERY specialized cell renderer takes
 * (`SpecializedCellProps`), dropping the row-action / detail-modal extras that
 * only `TitleCell` uses.
 *
 * None of the cell renderers is `React.memo`'d (the memo boundary in this tree
 * is `TableCell` / `TableRow`, above the router), so allocating this object per
 * cell costs nothing referentially — see validate:memo-stable-props.
 */
export function toSpecializedCellProps<TData extends BaseEntity>(
  props: CellRendererProps<TData>
): SpecializedCellProps<TData> {
  return {
    fieldId: props.fieldId,
    fieldValue: props.fieldValue,
    fieldConfig: props.fieldConfig,
    item: props.item,
    isEditing: props.isEditing,
    isTextareaField: props.isTextareaField,
    state: props.state,
    callbacks: props.callbacks,
    auth: props.auth,
    data: props.data,
    inlineEditHandler: props.inlineEditHandler,
    config: props.config,
  };
}
